import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Building2, Users, ShieldCheck, LifeBuoy, Activity, Moon } from "lucide-react";

const STATE_DOT = {
  green: "bg-emerald-400",
  amber: "bg-amber-400",
  red: "bg-rose-500",
  grey: "bg-slate-600",
};
const STATE_LABEL = { green: "Healthy", amber: "Warning / stale", red: "Failing", grey: "Not configured" };

function IntegrationLights({ items }) {
  // Reusable: renders whatever providers the API returns — no per-integration hardcoding.
  return (
    <div className="flex items-center gap-2 justify-end flex-wrap">
      {(items || []).map((it) => (
        <span
          key={it.key}
          className="inline-flex items-center gap-1.5 text-[11px] text-slate-400"
          title={`${it.label}: ${STATE_LABEL[it.state]}${it.detail ? ` — ${it.detail}` : ""}`}
          data-testid={`integration-${it.key}`}
        >
          <span className={`w-2.5 h-2.5 rounded-full ${STATE_DOT[it.state] || "bg-slate-600"}`} data-testid={`integration-${it.key}-${it.state}`} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

function fmtActivity(a) {
  if (!a || !a.last_activity) return "—";
  return a.last_activity.replace("T", " ").slice(0, 16);
}

function Stat({ icon: Icon, label, value, tone, testId }) {
  return (
    <div className="rounded-xl bg-slate-900/70 ring-1 ring-white/5 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-slate-400 text-xs font-medium uppercase tracking-wider">
        <Icon className={`w-4 h-4 ${tone || "text-emerald-400"}`} /> {label}
      </div>
      <div className="mt-2 text-3xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

export default function PlatformHealth() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/platform/health").then((r) => setData(r.data)).catch((e) => setErr(e.message));
  }, []);
  if (err) return <div className="p-8 text-rose-400">{err}</div>;
  if (!data) return <div className="p-8 text-slate-400">Loading platform health…</div>;
  const s = data.summary;
  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="platform-health-page">
      <div className="flex items-center gap-2 mb-1">
        <Activity className="w-5 h-5 text-emerald-400" />
        <h1 className="text-2xl font-bold tracking-tight">Platform Overview</h1>
      </div>
      <p className="text-slate-400 text-sm mb-8">Aggregate status &amp; metadata only — no credentials or tenant operational content is exposed here.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat icon={Building2} label="Companies" value={s.companies} testId="stat-companies" />
        <Stat icon={ShieldCheck} label="Active" value={s.active_companies} testId="stat-active" />
        <Stat icon={Building2} label="Suspended" value={s.suspended_companies} tone="text-amber-400" testId="stat-suspended" />
        <Stat icon={Moon} label="Dormant (30d)" value={s.dormant_companies ?? 0} tone="text-slate-400" testId="stat-dormant" />
        <Stat icon={Users} label="Users" value={s.users} tone="text-sky-400" testId="stat-users" />
        <Stat icon={ShieldCheck} label="Platform Admins" value={s.platform_admins} tone="text-emerald-400" testId="stat-admins" />
        <Stat icon={LifeBuoy} label="Active support sessions" value={s.active_support_sessions} tone="text-violet-400" testId="stat-sessions" />
      </div>

      <h2 className="text-lg font-semibold mb-3">Per-company usage</h2>
      <div className="overflow-x-auto rounded-xl ring-1 ring-white/5">
        <table className="w-full text-sm" data-testid="platform-health-table">
          <thead className="bg-slate-900/80 text-slate-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Last activity</th>
              <th className="text-right px-4 py-3">Active 7d / 30d</th>
              <th className="text-right px-4 py-3">Records</th>
              <th className="text-right px-4 py-3">Integrations</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {data.companies.map((c) => (
              <tr key={c.id} className="hover:bg-white/5" data-testid={`health-row-${c.slug}`}>
                <td className="px-4 py-3 font-medium">{c.name}<span className="text-slate-500 ml-1.5 text-xs">/{c.slug}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${c.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{c.status}</span>
                    {c.activity?.dormant && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-700/60 text-slate-300" data-testid={`dormant-flag-${c.slug}`}>
                        <Moon className="w-3 h-3" /> Dormant
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-300 tabular-nums text-xs" data-testid={`last-activity-${c.slug}`}>{fmtActivity(c.activity)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-300">{c.activity?.active_7d ?? 0} / {c.activity?.active_30d ?? 0}</td>
                <td className="px-4 py-3 text-right tabular-nums">{c.records_total.toLocaleString()}</td>
                <td className="px-4 py-3"><IntegrationLights items={c.integration_health} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

